/**
 * SignalDetailPage tile — full per-client roster for a smartbox's Peplink.
 *
 * Lazy-loaded on page open via `GET /api/boxes/{scu}/peplink` (the
 * snapshot also carries an embedded list, but the lazy endpoint gives
 * the freshest data + every field — useful here where we render the
 * full table with IP / connection type / hover-tooltip data).
 */
import React, { useEffect, useState } from "react";

import { DetailCard } from "../signal/DetailCard";
import { fetchBoxPeplink, type BoxPeplinkDetail } from "../../services/peplinkApi";
import { useAuth } from "../../contexts/AuthContext";
import { fmtAgo, fmtUtc } from "../../lib/format";

const ICON: Record<"hubspace" | "wifi" | "wired", string> = {
  hubspace: "🔌",
  wifi: "📶",
  wired: "🔗",
};

function kindOf(c: BoxPeplinkDetail["clients"][number]): keyof typeof ICON {
  if ((c.name ?? "").trim().toLowerCase() === "hubspace") return "hubspace";
  return c.is_hardwired ? "wired" : "wifi";
}

export interface PeplinkClientsCardProps {
  scu: string;
}

export const PeplinkClientsCard: React.FC<PeplinkClientsCardProps> = ({ scu }) => {
  const { token } = useAuth();
  const [data, setData] = useState<BoxPeplinkDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchBoxPeplink(token, scu)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, scu]);

  if (loading) {
    return (
      <DetailCard title="Peplink clients">
        <div className="text-xs text-secondary-500">loading…</div>
      </DetailCard>
    );
  }
  if (error) {
    return (
      <DetailCard title="Peplink clients">
        <div className="text-xs text-error-700">{error}</div>
      </DetailCard>
    );
  }
  if (!data || !data.device_sn) {
    return (
      <DetailCard title="Peplink clients">
        <div className="text-xs text-secondary-500">
          No Peplink tag-matched to this smartbox. Add{" "}
          <code className="px-1 py-0.5 bg-secondary-100 rounded">
            #{data?.smartbox_code ?? "SBxxx"}
          </code>{" "}
          to the device's InControl 2 tags to enable this view.
        </div>
      </DetailCard>
    );
  }

  const clients = data.clients ?? [];

  return (
    <DetailCard title="Peplink clients">
      <div className="text-xs text-secondary-500 mb-2">
        {data.device_name ?? data.device_sn}
        {data.status && (
          <>
            {" · "}
            <span
              className={
                data.status === "online"
                  ? "text-success-700"
                  : "text-secondary-500"
              }
            >
              {data.status}
            </span>
          </>
        )}
        {data.last_polled_utc && (
          <>
            {" · polled "}
            {fmtAgo(data.last_polled_utc)} ago
          </>
        )}
      </div>

      {clients.length === 0 ? (
        <div className="text-xs text-secondary-500">
          No clients on the last poll.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-secondary-600">
              <tr className="text-left">
                <th className="py-1 pr-2"></th>
                <th className="py-1 pr-2">Name</th>
                <th className="py-1 pr-2">IP</th>
                <th className="py-1 pr-2">Type</th>
                <th className="py-1 pr-2">MAC</th>
                <th className="py-1 pr-2">Last seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {clients.map((c) => {
                const k = kindOf(c);
                return (
                  <tr key={c.mac} className="hover:bg-secondary-50">
                    <td
                      className="py-1 pr-2 text-base"
                      title={
                        k === "hubspace"
                          ? "Hubspace smart plug"
                          : c.is_hardwired
                            ? "Hardwired"
                            : "Wireless"
                      }
                    >
                      {ICON[k]}
                    </td>
                    <td className="py-1 pr-2 font-medium">
                      {c.name?.trim() || "—"}
                    </td>
                    <td className="py-1 pr-2 font-mono text-secondary-700">
                      {c.ip ?? "—"}
                    </td>
                    <td className="py-1 pr-2 text-secondary-600">
                      {c.connection_type ?? "—"}
                    </td>
                    <td className="py-1 pr-2 font-mono text-[11px] text-secondary-500">
                      {c.mac}
                    </td>
                    <td className="py-1 pr-2 text-secondary-500">
                      {c.last_seen_utc ? fmtUtc(c.last_seen_utc) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </DetailCard>
  );
};

export default PeplinkClientsCard;
